import { z } from 'zod';
import { isValidPhoneNumber } from 'libphonenumber-js/mobile';

/**
 * Base schema used by enhanceAction for server-side parsing.
 *
 * Intentionally avoids .refine() calls because enhanceAction requires a plain
 * ZodObject (not ZodEffects). Phone format is still validated by the full
 * IntakeFormSchema used by the react-hook-form resolver on the client.
 */
export const IntakeFormBaseSchema = z.object({
  planId: z.string().uuid('Invalid plan'),
  name: z
    .string()
    .max(255)
    .refine(
      (val) => {
        // Accept anything with at least two whitespace-separated, non-empty
        // parts. Looser than letters-only on purpose so real names like
        // "Mary-Jane O'Connor", "Smith Jr.", or accented characters pass.
        const parts = val.trim().split(/\s+/).filter(Boolean);
        return parts.length >= 2;
      },
      { message: 'Please enter your first and last name.' },
    ),
  phoneNumber: z.string().min(1, 'Phone number is required'),
  email: z
    .string()
    .email('Please enter a valid email address')
    .optional()
    .or(z.literal('')),
  customFields: z.array(
    z.object({
      fieldId: z.string().uuid(),
      value: z.union([
        z.object({ text: z.string() }),
        z.object({ number: z.number() }),
        z.object({ date: z.string() }),
        z.object({ select: z.string() }),
        z.object({ boolean: z.boolean() }),
        z.object({ file: z.string() }),
        // Tour field: the prospect's picked slot. Consumed by
        // submitMembershipIntakeAction to create the free trainer booking.
        z.object({
          tour: z.object({
            serviceId: z.string().uuid(),
            courtId: z.string().uuid(),
            trainerId: z.string().uuid(),
            bookingDate: z.string(),
            startTime: z.string(),
            endTime: z.string(),
            slotDuration: z.number(),
            isPeak: z.boolean(),
          }),
        }),
      ]),
    })
      // RHF's setValue('customFields.${index}', ...) creates sparse arrays —
      // untouched indices land here as `undefined`. The form-level onSubmit
      // filters undefined entries out and runs its own required-field check
      // against the original customFields definition, so accepting undefined
      // here just lets the resolver pass through to that logic instead of
      // surfacing a misleading "Required" on optional fields the user
      // intentionally skipped.
      .nullish(),
  )
    // Bound the array: a public endpoint shouldn't accept thousands of entries
    // (each tour selection can trigger a booking). Real intakes have a handful.
    .max(50, 'Too many fields')
    .default([]),
  captchaToken: z.string().default(''),
});

/**
 * Full schema with E.164 phone refinement used by the client-side form resolver.
 * Not passed to enhanceAction (which cannot accept ZodEffects).
 */
export const IntakeFormSchema = IntakeFormBaseSchema.superRefine((data, ctx) => {
  if (!data.phoneNumber || !isValidPhoneNumber(data.phoneNumber)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please enter a valid phone number for the selected country',
      path: ['phoneNumber'],
    });
  }
});

export type IntakeFormData = z.infer<typeof IntakeFormBaseSchema>;

/**
 * Shape of a membership plan returned by get_intake_form_data
 */
export interface IntakePlan {
  id: string;
  rate_group_id?: string | null;
  guest_policy?: unknown;
  location_id?: string | null;
  requires_approval?: boolean;
  access_start_time?: string | null;
  access_end_time?: string | null;
  advance_booking_days?: number | null;
  fixed_slot_advance_booking_days?: number | null;
  has_perks?: boolean;
  name: string;
  description: string | null;
  description_alt: string | null;
  period: string;
  price: number;
  benefits: string[];
  benefits_alt: string[];
  image_url: string | null;
  color: string | null;
  display_order: number;
}

/**
 * Shape of a custom field returned by get_intake_form_data
 */
export interface IntakeCustomField {
  id: string;
  name: string;
  label: string;
  field_type: 'text' | 'number' | 'date' | 'select' | 'boolean' | 'file' | 'tour';
  description: string | null;
  options: string[];
  is_required: boolean;
  display_order: number;
  for_parent: boolean;
  for_child: boolean;
}

/**
 * Shape of the data returned by loadIntakeFormData
 */
export interface IntakeFormPageData {
  plans: IntakePlan[];
  customFields: IntakeCustomField[];
  businessName: string | null;
}
